/*
 * Copyright (c) 2008-2019 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

import Vue from "vue"
import api from "@/config/api"

export default {
  namespaced: true,

  state: {
    userAutoSelectionRegistered: null,
    userAutoSelectionActive: null,
    status: null,
  },

  mutations: {
    /**
     * commit userAutoSelectionRegistration status to state
     * (whether or not AutoSelectUserStrategy is contained within the configured SelectUserStrategies)
     *
     */
    userAutoSelectionRegistered(state, registered) {
      state.userAutoSelectionRegistered = registered
    },

    /**
     * commit userAutoSelection activation status to state
     * (whether or not AutoSelectUserStrategy is enabled)
     */
    userAutoSelectionActive(state, active) {
      state.userAutoSelectionActive = active
    },

    /**
     * commit load error to state
     */
    loadError(state) {
      state.status = "error"
    },

    /**
     * commit load success to state
     */
    loadSuccess(state) {
      state.status = "success"
    },
  },

  actions: {
    /**
     * Load all settings
     */
    loadSettings({ commit }) {
      Vue.http.get(api.USER_AUTO_SELECTION()).then(
        response => {
          commit("userAutoSelectionRegistered", response.body.registered)
          commit("userAutoSelectionActive", response.body.active)
          commit("loadSuccess")
        },
        () => {
          commit("loadError")
          //ToDo: log load error
        }
      )
    },

    /**
     * Trigger reload of users from user service
     *
     * @returns {Promise<any>}
     */
    // eslint-disable-next-line no-unused-vars
    reloadUsers({ commit }, list) {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.USER_RELOADS(), list).then(
          response => {
            resolve(response.data)
          },
          response => {
            reject(response)
          }
        )
      })
    },

    /**
     * Trigger reset of user auto-selection
     *
     * @returns {Promise<any>}
     */
    resetUserAutoSelection() {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.USER_AUTO_SELECTION_RESET()).then(
          response => {
            resolve(response.data)
          },
          response => {
            reject(response)
          }
        )
      })
    },

    /**
     * Trigger deactivation of user auto-selection
     *
     * @returns {Promise<any>}
     */
    disableUserAutoSelection({ commit }) {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.USER_AUTO_SELECTION_DEACTIVATION()).then(
          response => {
            commit("userAutoSelectionActive", false)
            resolve(response.data)
          },
          response => {
            reject(response)
          }
        )
      })
    },

    /**
     * Trigger activation of user auto-selection
     *
     * @returns {Promise<any>}
     */
    enableUserAutoSelection({ commit }) {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.USER_AUTO_SELECTION_ACTIVATION()).then(
          response => {
            commit("userAutoSelectionActive", true)
            resolve(response.data)
          },
          response => {
            reject(response)
          }
        )
      })
    },

    /**
     * Delete users from system
     * @param commit
     * @param userIds represents the list of userIds that are going to be deleted
     * @returns {Promise<any>}
     */
    deleteUsers({}, { userIds }) {
      return new Promise((resolve, reject) => {
        userIds.forEach(id => {
          Vue.http.delete(api.USER_DELETE_USER(id)).then(
            response => {
              resolve(response.data)
            },
            response => {
              reject(response)
            }
          )
        })
      })
    },

    /**
     * Delete all users from system
     * @returns {Promise<any>}
     */
    deleteAllUsers() {
      return new Promise((resolve, reject) => {
        Vue.http.delete(api.USER_DELETE_ALL_USERS()).then(
          response => {
            resolve(response.data)
          },
          response => {
            reject(response)
          }
        )
      })
    },
  },

  getters: {
    /**
     * Get whether settings have successfully loaded
     *
     * @returns {boolean}
     */
    hasLoaded: state => state.status === "success",

    /**
     * Get whether user auto-selection is registered
     *
     * @returns {boolean}
     */
    userAutoSelectionRegistered: state => state.userAutoSelectionRegistered,

    /**
     * Get whether user auto-selection is active
     *
     * @returns {boolean}
     */
    userAutoSelectionActive: state => state.userAutoSelectionActive,
  },
}
